/**
 * Helpers for UserFormDialog submit payload construction (complexity extraction).
 */

import authService from '../auth/authService';
import userService from '../services/userService';
import { mapFormValuesToCreateRequest, toPostgresArrayLiteral } from '../utils/userMapper';
import { getRISRoleString } from '../utils/roleUtils';
import type { Gender, User, UserCreateRequest, UserRole, UserStatus } from '../types/user.types';

function toGender(value: string): Gender {
  if (value === 'Male' || value === 'Female' || value === 'Other') {
    return value;
  }
  return 'Other';
}

function toUserRole(value: string): UserRole {
  if (value === 'Technician' || value === 'Radiologist' || value === 'ReferringPhysician') {
    return value;
  }
  return 'Technician';
}

function toUserStatus(value: string): UserStatus {
  return value === 'Inactive' ? 'Inactive' : 'Active';
}

export function parseCsvValues(value: string): string[] {
  return (value || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function splitCsvOrUndefined(value: string | undefined): string[] | undefined {
  if (!value) {
    return undefined;
  }
  const parts = parseCsvValues(value);
  return parts.length ? parts : undefined;
}

export type SubmitFormFields = Record<string, string>;

export type SubmitContext = {
  f: SubmitFormFields;
  mobileForPayload: string;
  employeeIdTrimmed: string;
  experienceYears: number | undefined;
  facilityId?: string;
  initialUser?: User | null;
  signatureFile: File | null;
  signatureCleared: boolean;
};

function buildSharedRadiologistFormValues(ctx: SubmitContext) {
  const { f, mobileForPayload, employeeIdTrimmed, experienceYears, signatureFile } = ctx;
  return {
    fullName: f.fullName.trim(),
    gender: f.gender,
    dateOfBirth: f.dateOfBirth,
    username: f.username.trim(),
    email: f.email.trim(),
    phoneNo: mobileForPayload,
    password: f.password,
    role: f.role,
    sub_role: f.subRole || undefined,
    status: f.status === 'Active' ? 'active' : 'inactive',
    employeeId: employeeIdTrimmed,
    qualification: f.qualification?.trim() || undefined,
    experienceYears,
    reportingModalityAccess: splitCsvOrUndefined(f.modalityAccess),
    registrationNo: f.registrationNumber?.trim() || undefined,
    specialty: splitCsvOrUndefined(f.specialty),
    designation: f.designation?.trim() || undefined,
    signature: signatureFile || undefined,
    shiftStart: f.shiftStartAt?.trim() || undefined,
    shiftEnd: f.shiftEndAt?.trim() || undefined,
    specialization: splitCsvOrUndefined(f.specialization),
    clinicHospitalName: f.clinicHospitalName?.trim() || undefined,
    reportAccess: f.reportAccess === 'true',
    availabilityDays: splitCsvOrUndefined(f.availabilityDays),
  };
}

function applyCommonOptionalPayloadFields(
  payload: Partial<UserCreateRequest> & Record<string, unknown>,
  f: SubmitFormFields,
  experienceYears: number | undefined
): void {
  if (f.designation) {
    payload.designation = f.designation;
  }
  if (f.registrationNumber) {
    payload.registration_number = f.registrationNumber;
  }
  if (f.qualification) {
    payload.qualification = f.qualification;
  }
  if (experienceYears !== undefined) {
    payload.experience_years = experienceYears;
  }
}

function applyEmployeeAndRoleIds(
  payload: Partial<UserCreateRequest> & Record<string, unknown>,
  f: SubmitFormFields,
  employeeIdTrimmed: string
): void {
  if (f.role !== 'ReferringPhysician') {
    payload.employee_id = f.employeeId || '';
  }
  if (f.role !== 'Radiologist') {
    return;
  }
  payload.doctor_id = employeeIdTrimmed;
  if (f.subRole) {
    payload.sub_role = f.subRole;
  }
}

function applyShiftFieldsForTechOrRadiologist(
  payload: Partial<UserCreateRequest> & Record<string, unknown>,
  f: SubmitFormFields
): void {
  if (f.role !== 'Technician' && f.role !== 'Radiologist') {
    return;
  }
  if (f.shiftStartAt) {
    payload.shift_start_at = f.shiftStartAt;
  }
  if (f.shiftEndAt) {
    payload.shift_end_at = f.shiftEndAt;
  }
}

function applyRadiologistSpecialtyAndAvailability(
  payload: Partial<UserCreateRequest> & Record<string, unknown>,
  f: SubmitFormFields
): void {
  if (f.role !== 'Radiologist') {
    return;
  }
  if (f.specialty) {
    const specialtyValues = parseCsvValues(f.specialty);
    if (specialtyValues.length) {
      payload.specialty = toPostgresArrayLiteral(specialtyValues);
    }
  }
  if (!f.availabilityDays) {
    return;
  }
  const days = parseCsvValues(f.availabilityDays);
  if (days.length) {
    payload.availability_days = days;
  }
}

function applyReferringPhysicianFields(
  payload: Partial<UserCreateRequest> & Record<string, unknown>,
  f: SubmitFormFields
): void {
  if (f.role !== 'ReferringPhysician') {
    return;
  }
  if (f.specialization) {
    const specializationValues = parseCsvValues(f.specialization);
    if (specializationValues.length) {
      payload.specialization = toPostgresArrayLiteral(specializationValues);
    }
  }
  if (f.clinicHospitalName) {
    payload.clinic_hospital_name = f.clinicHospitalName;
  }
  payload.report_access = f.reportAccess === 'true';
}

function applyClearedRadiologistSignature(
  payload: Partial<UserCreateRequest> & Record<string, unknown>,
  ctx: SubmitContext
): void {
  const { f, signatureCleared, initialUser, signatureFile } = ctx;
  if (f.role === 'Radiologist' && signatureCleared && initialUser?.signature && !signatureFile) {
    payload.signature = null;
  }
}

function applyRoleSpecificEditPayload(
  payload: Partial<UserCreateRequest> & Record<string, unknown>,
  ctx: SubmitContext
): void {
  const { f, employeeIdTrimmed, experienceYears } = ctx;

  applyEmployeeAndRoleIds(payload, f, employeeIdTrimmed);
  applyShiftFieldsForTechOrRadiologist(payload, f);
  applyCommonOptionalPayloadFields(payload, f, experienceYears);
  applyRadiologistSpecialtyAndAvailability(payload, f);

  const modalityArr = parseCsvValues(f.modalityAccess || '');
  if (modalityArr.length) {
    payload.reporting_modality_access = modalityArr;
  }

  applyReferringPhysicianFields(payload, f);
  applyClearedRadiologistSignature(payload, ctx);
}

export function buildEditJsonPayload(
  ctx: SubmitContext
): Partial<UserCreateRequest> & Record<string, unknown> {
  const { f, mobileForPayload } = ctx;
  const departmentArr = parseCsvValues(f.department || '');
  const payload: Partial<UserCreateRequest> & Record<string, unknown> = {
    full_name: f.fullName,
    gender: toGender(f.gender),
    date_of_birth: f.dateOfBirth,
    username: f.username,
    email: f.email,
    mobile_number: mobileForPayload.slice(-10),
    role: toUserRole(f.role),
    status: toUserStatus(f.status),
    department: departmentArr.length ? departmentArr : [],
  };
  applyRoleSpecificEditPayload(payload, ctx);
  return payload;
}

export async function submitEditWithSignature(
  ctx: SubmitContext,
  editFacilityId: string,
  getDobForPayload: (dob: string | undefined) => string
): Promise<{ success: boolean; data?: User; error?: string }> {
  const { signatureFile, initialUser } = ctx;
  if (!signatureFile || initialUser?.id == null) {
    return { success: false, error: 'Missing signature or user.' };
  }
  const values = {
    ...buildSharedRadiologistFormValues(ctx),
    dateOfBirth: getDobForPayload(ctx.f.dateOfBirth),
    signature: signatureFile,
  };
  const formData = await mapFormValuesToCreateRequest(
    values,
    editFacilityId,
    true,
    getRISRoleString(authService.getUser())
  );
  return userService.updateUser(initialUser.id, formData);
}

export async function submitEditJson(
  ctx: SubmitContext,
  getDobForPayload: (dob: string | undefined) => string
): Promise<{ success: boolean; data?: User; error?: string }> {
  const userId = ctx.initialUser?.id;
  if (userId == null) {
    return { success: false, error: 'Missing user id.' };
  }
  const payload = buildEditJsonPayload(ctx);
  payload.date_of_birth = getDobForPayload(ctx.f.dateOfBirth);
  return userService.updateUser(userId, payload);
}

export async function submitCreateUser(
  ctx: SubmitContext,
  getDobForPayload: (dob: string | undefined) => string
): Promise<{ success: boolean; data?: User; error?: string }> {
  const { f, facilityId, mobileForPayload, employeeIdTrimmed, experienceYears, signatureFile } =
    ctx;
  const values = {
    fullName: f.fullName.trim(),
    gender: f.gender,
    dateOfBirth: getDobForPayload(f.dateOfBirth),
    username: f.username.trim(),
    email: f.email.trim(),
    phoneNo: mobileForPayload,
    password: f.password,
    role: f.role,
    sub_role: f.subRole || undefined,
    status: f.status === 'Active' ? 'active' : 'inactive',
    employeeId: f.role === 'ReferringPhysician' ? '' : employeeIdTrimmed,
    qualification: f.qualification?.trim() || undefined,
    experienceYears,
    reportingModalityAccess: splitCsvOrUndefined(f.modalityAccess),
    registrationNo: f.registrationNumber?.trim() || undefined,
    specialty: splitCsvOrUndefined(f.specialty),
    designation: f.designation?.trim() || undefined,
    signature: signatureFile || undefined,
    shiftStart: f.shiftStartAt?.trim() || undefined,
    shiftEnd: f.shiftEndAt?.trim() || undefined,
    specialization: splitCsvOrUndefined(f.specialization),
    clinicHospitalName: f.clinicHospitalName?.trim() || undefined,
    reportAccess: f.reportAccess === 'true',
    availabilityDays:
      f.role === 'Radiologist' ? splitCsvOrUndefined(f.availabilityDays) : undefined,
  };
  if (!facilityId) {
    return { success: false, error: 'Facility is required.' };
  }
  const formData = await mapFormValuesToCreateRequest(values, facilityId);
  return userService.createUser(formData);
}

export function resolveEditFacilityId(
  facilityId: string | undefined,
  initialUser: User | null | undefined
): string {
  if (facilityId?.trim()) {
    return facilityId.trim();
  }
  if (initialUser?.facility_id != null && String(initialUser.facility_id).trim() !== '') {
    return String(initialUser.facility_id);
  }
  return '';
}

export type UserFormSubmitResult = { success: boolean; data?: User; error?: string };

export function buildSubmitContext(params: {
  f: SubmitFormFields;
  facilityId?: string;
  initialUser?: User | null;
  signatureFile: File | null;
  signatureCleared: boolean;
  getMobileDigits: (raw: string | undefined) => string;
}): SubmitContext {
  const { f, facilityId, initialUser, signatureFile, signatureCleared, getMobileDigits } = params;
  const mobileDigits = getMobileDigits(f.mobileNumber);
  const mobileForPayload = mobileDigits.slice(-10);
  const employeeIdTrimmed = (f.employeeId || '').trim();
  const expNum = f.experience ? Number.parseInt(f.experience, 10) : undefined;
  const experienceYears = expNum !== undefined && !Number.isNaN(expNum) ? expNum : undefined;
  return {
    f,
    mobileForPayload,
    employeeIdTrimmed,
    experienceYears,
    facilityId,
    initialUser,
    signatureFile,
    signatureCleared,
  };
}

export async function runUserFormSubmit(params: {
  isEdit: boolean;
  ctx: SubmitContext;
  facilityId: string | undefined;
  getDobForPayload: (dob: string | undefined) => string;
}): Promise<{ result?: UserFormSubmitResult; error?: string; abort?: boolean }> {
  const { isEdit, ctx, facilityId, getDobForPayload } = params;
  const { f } = ctx;

  if (isEdit) {
    const editFacilityId = resolveEditFacilityId(facilityId, ctx.initialUser);
    if (f.role === 'Radiologist' && ctx.signatureFile) {
      if (!editFacilityId) {
        return { error: 'Facility is required to update signature.', abort: true };
      }
      return {
        result: await submitEditWithSignature(ctx, editFacilityId, getDobForPayload),
      };
    }
    return { result: await submitEditJson(ctx, getDobForPayload) };
  }

  if (!facilityId) {
    return { error: 'Facility is required. Please select a facility.', abort: true };
  }
  return { result: await submitCreateUser(ctx, getDobForPayload) };
}

export type UserFormSubmitGate =
  | { ok: false; kind: 'validation'; errors: Record<string, string> }
  | { ok: false; kind: 'submitError'; message: string }
  | { ok: false; kind: 'abort' }
  | { ok: true };

export function evaluateUserFormSubmitGate(params: {
  f: SubmitFormFields;
  isEdit: boolean;
  initialUserId?: number | string | null;
  signatureFile: File | null;
  getEditUserValidationErrors: (
    f: SubmitFormFields,
    opts: { requireSubRole: boolean }
  ) => Record<string, string>;
  getCreateUserValidationErrors: (
    f: SubmitFormFields,
    opts: { requireSubRole: boolean }
  ) => Record<string, string>;
  isAllowedSignatureUploadFile: (file: File) => boolean;
  signatureUploadTypeError: string;
}): UserFormSubmitGate {
  const {
    f,
    isEdit,
    initialUserId,
    signatureFile,
    getEditUserValidationErrors,
    getCreateUserValidationErrors,
    isAllowedSignatureUploadFile,
    signatureUploadTypeError,
  } = params;

  const validationOptions = { requireSubRole: f.role === 'Radiologist' };
  const validationErrors = isEdit
    ? getEditUserValidationErrors(f, validationOptions)
    : getCreateUserValidationErrors(f, validationOptions);

  if (Object.keys(validationErrors).length > 0) {
    return { ok: false, kind: 'validation', errors: validationErrors };
  }

  if (
    f.role === 'Radiologist' &&
    signatureFile instanceof File &&
    !isAllowedSignatureUploadFile(signatureFile)
  ) {
    return { ok: false, kind: 'submitError', message: signatureUploadTypeError };
  }

  if (isEdit && !initialUserId) {
    return { ok: false, kind: 'abort' };
  }

  return { ok: true };
}

export function applyUserFormSubmitResult(params: {
  result: UserFormSubmitResult;
  isEdit: boolean;
  onSuccess: (user: User) => void;
  handleClose: () => void;
  clearSignatureFile: () => void;
  setSubmitError: (msg: string | null) => void;
}): void {
  const { result, isEdit, onSuccess, handleClose, clearSignatureFile, setSubmitError } = params;
  if (result.success && result.data) {
    clearSignatureFile();
    onSuccess(result.data);
    handleClose();
    return;
  }
  setSubmitError(result.error || (isEdit ? 'Failed to update user' : 'Failed to create user'));
}
